import React from 'react'
import { useEffect, useSelector, useDispatch, useState, useMatch } from 'hooks'
import { Button, T, Checkbox, Select, CriteriaSelect, UpgradeButton } from 'components'
import { searchCriteria, criteriaIds, criteriaName } from 'common/criteria'
import { fetchRegistry } from 'store/actions'
import { isGold, toQueryString } from 'utils'
import { UpgradeReason } from 'pages/Payment/Upgrade/StyledUpgrade.js'

import StyledForm, { StyledCriteria, StyledFilterGroup } from './StyledForm'

const Criteria = ({ disabled, children, className, label, name }) => (
  <StyledCriteria className={className + ' criteria'} disabled={disabled}>
    {name && <div className="criteria-name">{name}</div>}
    {label && <T as="div" className="criteria-name" label={label} />}
    <div className="criteria-controls">{children}</div>
  </StyledCriteria>
)

const FilterGroup = ({ children, label }) => (
  <StyledFilterGroup>
    {label && <T as="div" className="group-name" label={label} />}
    <div className="group-filters row">{children}</div>
  </StyledFilterGroup>
)

const cleanQuery = (search, orientation) => {
  const data = {}
  Object.entries(search)
    .filter(e => e[1] && e[1].length)
    .forEach(([name, value]) => {
      data[name] = value.toString()
    })
  data.seekingOrientationId = orientation.toString()
  if (!data.seekingCountryId) {
    data.seekingCountryId = '0'
    delete data.seekingRegionId
    delete data.seekingCityId
  }
  return data
}

const Submit = ({ search, orientation, className, onClick }) => {
  const query = toQueryString(cleanQuery(search, orientation))
  return (
    <Criteria className={`submit-criteria ${className || ''}`}>
      <Button className="submit-button" onClick={onClick} to={`/find-date/${query}`} label="actions/search" />
    </Criteria>
  )
}

const seekingName = name => name.replace(/^lf/, 'seeking').replace(/Ids$/, 'Id')

const defaultSearch = md => Object.fromEntries(Object.entries(md).map(([key, value]) => [seekingName(key), value]))

const useSearch = query => {
  const [search, setSearch] = useState({})
  const md = useSelector(s => s.myProfile.matchDescriptor)

  useEffect(() => {
    setSearch({
      ...search,
      ...defaultSearch(md),
      withGallery: query.withGallery,
      onlineOnly: query.onlineOnly,
      // TODO: other filters not taken from query
    })
  }, [])

  const onChange = ({ target: el }) =>
    setSearch({
      ...search,
      [el.name]: /checkbox/.test(el.type) && !el.checked ? '' : el.value.split(',').filter(e => e),
    })

  return [search, onChange]
}

const Form = ({ onSubmit }) => {
  const dispatch = useDispatch()
  const query = useSelector(s => s.router.location.query)
  const upgraded = useSelector(s => isGold(s.myProfile))
  const registry = useSelector(s => s.registry)
  const nicheType = useSelector(s => s.flags.adult) ? 'adult' : 'dating'

  // age, sex, orientations take query into account - others don't (for now)
  const { ageFrom, ageTo, sexIds, orientationIds, countries, regions, cities } = useMatch(query)
  const [search, onChange] = useSearch(query)

  useEffect(() => {
    registry.sexes?.length || dispatch(fetchRegistry())
  }, [])

  return (
    <StyledForm onChange={onChange} upgraded={upgraded}>
      <div className="basic-filters">
        <div className="row">
          <Criteria label="seeking">
            <Select name="seekingSexId" multiSelect {...sexIds} />
          </Criteria>
          <Criteria label="age" className="criteria-age">
            <Select name="seekingAgeFrom" {...ageFrom} />
            <T as="span" className="age-separator" label="to" />
            <Select name="seekingAgeTo" {...ageTo} />
          </Criteria>
          <Criteria label="country">
            <Select name="seekingCountryId" multiSelect empty="ALL_COUNTRIES" {...countries} />
          </Criteria>
          {nicheType == 'adult' ? (
            <Criteria label="orientation">
              <Select name="seekingOrientationId" multiSelect {...orientationIds} />
            </Criteria>
          ) : (
            <div></div>
          )}
        </div>
        <div className="row second-row">
          <Submit className="basic-submit" search={search} onClick={onSubmit} orientation={orientationIds.selected} />
          <div className="show-profiles">
            <T as="div" className="criteria-name" label="onlyWith" />
            <div className="checklist">
              <Checkbox name="withGallery" label="withGallery" defaultChecked={!!query.withGallery} />
              <Checkbox name="onlineOnly" label="onlineOnly" defaultChecked={!!query.onlineOnly} />
            </div>
          </div>
        </div>
      </div>

      <div className="advanced-filters">
        {!upgraded && (
          <div className="trial-overlay">
            <T as={UpgradeReason} className="overlay-header" label="membership/upgradeToUnlock2" />
            <div id="lock">
              <UpgradeButton className="button-cta" size="large" />
            </div>
            <T as="p" label="membership/upgradeToUnlock1" />
          </div>
        )}
        <FilterGroup label="membership/disallowedText|clean">
          <Criteria disabled={!upgraded} label="region">
            <Select disabled={!upgraded} name="seekingRegionId" multiSelect empty="ALL_REGIONS" {...regions} />
          </Criteria>
          <Criteria disabled={!upgraded} label="city">
            <Select disabled={!upgraded} name="seekingCityId" multiSelect empty="ALL_CITIES" {...cities} />
          </Criteria>
          <Criteria disabled={!upgraded} label="seekingSimple">
            <CriteriaSelect
              disabled={!upgraded}
              group="criteria"
              id={criteriaIds.lookingFor[nicheType]}
              multiSelect
              empty
            />
          </Criteria>
          <Criteria disabled={!upgraded} label="interests">
            <CriteriaSelect
              disabled={!upgraded}
              group="criteria"
              id={criteriaIds.interests[nicheType]}
              multiSelect
              empty
            />
          </Criteria>
        </FilterGroup>

        <FilterGroup label="aboutMyMatch">
          {searchCriteria.map(id => (
            <Criteria disabled={!upgraded} key={id} name={criteriaName(id, registry)}>
              <CriteriaSelect disabled={!upgraded} group="criteria" id={id} multiSelect empty />
            </Criteria>
          ))}
        </FilterGroup>
      </div>
    </StyledForm>
  )
}

export default Form
